/**
 * ToolkitPage — Grid of communication tools
 *
 * Displays all tools with icon, name, description, and
 * character attribution. Links to individual tool pages.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import * as LucideIcons from 'lucide-react';

import Card from '../components/shared/Card';
import CharacterAvatar from '../components/characters/CharacterAvatar';
import { TOOLS } from '../data/toolkit';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.4 },
  }),
};

const ToolkitPage = () => {
  return (
    <>
      <Helmet>
        <title>Toolkit | Finding Your Voice</title>
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-10">
        <motion.div
          className="text-center mb-8 rounded-2xl bg-gradient-to-br from-theron-bg/40 via-courtyard-parchment to-xenia-bg/30 border border-courtyard-terracotta/15 px-6 py-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <h1 className="font-display text-3xl md:text-4xl font-bold text-courtyard-stone">
            Your Toolkit
          </h1>
          <p className="mt-2 text-courtyard-stone/80">Practical tools from your three guides to use before, during, and after speaking</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {TOOLS.map((tool, i) => {
            const IconComponent = LucideIcons[tool.icon] || LucideIcons.Wrench;

            return (
              <motion.div
                key={tool.id}
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={i}
              >
                <Link to={`/toolkit/${tool.id}`}>
                  <Card
                    variant={tool.character}
                    className={`p-5 h-full hover:shadow-courtyard-md transition-all hover:-translate-y-0.5 border-l-4 ${
                      tool.character === 'philemon' ? 'border-l-courtyard-olive' :
                      tool.character === 'xenia' ? 'border-l-courtyard-aegean' :
                      tool.character === 'all' ? 'border-l-courtyard-gold' :
                      'border-l-courtyard-terracotta'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                        tool.character === 'philemon' ? 'bg-courtyard-olive/15' :
                        tool.character === 'xenia' ? 'bg-courtyard-aegean/15' :
                        tool.character === 'all' ? 'bg-courtyard-gold/15' :
                        'bg-courtyard-terracotta/15'
                      }`}>
                        <IconComponent className={`w-5 h-5 ${
                          tool.character === 'philemon' ? 'text-courtyard-olive-dark' :
                          tool.character === 'xenia' ? 'text-courtyard-aegean-dark' :
                          tool.character === 'all' ? 'text-courtyard-gold-ink' :
                          'text-courtyard-clay'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-display font-bold text-courtyard-stone">
                          {tool.name}
                        </h3>
                        <p className="text-sm text-courtyard-stone/80 mt-1 leading-relaxed">
                          {tool.description}
                        </p>
                        <div className="flex items-center gap-2 mt-3">
                          <CharacterAvatar characterId={tool.character} size="xs" />
                          <span className="text-sm text-courtyard-stone/80 capitalize">
                            {tool.character === 'all' ? 'All three guides' : tool.character}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default ToolkitPage;
